/**
 * Dados fictícios para o mockup do módulo Diretoria (pré-visualização).
 * KPIs executivos e parte dos gráficos podem ser enriquecidos com `/reports/dashboard` na página.
 */

import type { DiretoriaExecutiveCard } from '@/lib/diretoriaDashboard';
import type { DiretoriaKpiTone } from '@/lib/diretoriaKpiTone';

export type DiretoriaChartKind = 'line' | 'bar' | 'pie' | 'none';

/** Destino do link “Clique aqui” no card de apoio Rique.Ia (Diretoria). */
export const DIRETORIA_RIQUE_IA_URL = 'https://rique.ia';

export interface DiretoriaKpi {
  label: string;
  value: string;
  hint?: string;
  /** Verde = bom; vermelho = risco / atenção; omitir = neutro. */
  tone?: DiretoriaKpiTone;
}

/** Linhas de série para gráficos line/bar: eixo X = `name` */
export interface DiretoriaSeriesPoint {
  name: string;
  valor: number;
  valor2?: number;
}

export interface DiretoriaPieSlice {
  name: string;
  value: number;
}

export interface DiretoriaIndicatorMock {
  id: string;
  title: string;
  shortDescription: string;
  /** Índice em `kpis` destacado no cartão (padrão: 0) */
  primaryKpiIndex?: number;
  kpis: DiretoriaKpi[];
  chartKind: DiretoriaChartKind;
  /** line / bar */
  series?: DiretoriaSeriesPoint[];
  /** pie */
  pieData?: DiretoriaPieSlice[];
  barSeriesKey?: 'valor' | 'valor2';
  tableHeaders: string[];
  tableRows: string[][];
  footnote: string;
}

const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/** Ordem canônica de status da frota (gráfico, tabela e KPIs alinhados). */
export const DIRETORIA_MOTO_STATUS_ORDER = [
  'Apropriação Indébita',
  'Furto/Roubo',
  'Alugada',
  'Apreendida',
  'Bloqueada',
  'Disponível',
  'Em manutenção',
  'PT',
  'Vendida',
] as const;

/** Contagens demo por status (soma = frota base). */
const MOTOS_STATUS_BASE_COUNTS = [3, 2, 198, 5, 8, 86, 14, 42, 54] as const;

function serieBase(seed: number): DiretoriaSeriesPoint[] {
  return meses.map((name, i) => ({
    name,
    valor: Math.round(40 + seed * 3 + Math.sin(i / 2) * 18 + i * 2),
    valor2: Math.round(25 + seed * 2 + Math.cos(i / 3) * 12 + i),
  }));
}

function getScenarioScale(companyId: string, periodId: string): number {
  let c = 1;
  if (companyId === 'jp') c *= 0.92;
  if (companyId === 'natal') c *= 0.88;
  let p = 1;
  if (periodId === 'ano') p *= 0.96;
  if (periodId === '12m') p *= 1.04;
  return c * p;
}

function buildMotosPorStatusIndicator(scale: number): DiretoriaIndicatorMock {
  const pieData: DiretoriaPieSlice[] = DIRETORIA_MOTO_STATUS_ORDER.map((name, i) => ({
    name,
    value: Math.max(0, Math.round(MOTOS_STATUS_BASE_COUNTS[i] * scale)),
  }));
  const total = pieData.reduce((s, x) => s + x.value, 0);
  const largest = [...pieData].sort((a, b) => b.value - a.value)[0];
  const criticalSet = new Set(['Apropriação Indébita', 'Furto/Roubo', 'Apreendida']);
  const critical = pieData.filter((x) => criticalSet.has(x.name)).reduce((s, x) => s + x.value, 0);
  const goodShare = new Set(['Alugada', 'Disponível', 'Vendida']);
  const maiorFatiaTone: DiretoriaKpiTone = largest
    ? criticalSet.has(largest.name)
      ? 'bad'
      : goodShare.has(largest.name)
        ? 'good'
        : 'neutral'
    : 'neutral';

  return {
    id: 'motos-status',
    title: 'Motos por status',
    shortDescription: 'Distribuição atual da frota por status operacional (exemplo).',
    primaryKpiIndex: 0,
    kpis: [
      { label: 'Total na base', value: String(total), hint: 'mock', tone: 'neutral' },
      {
        label: 'Maior fatia',
        value: largest ? `${largest.name} (${total ? ((largest.value / total) * 100).toFixed(1) : '0'}%)` : '—',
        hint: 'mock',
        tone: maiorFatiaTone,
      },
      {
        label: 'Situações críticas',
        value: String(critical),
        hint: 'Apropriação + Furto/Roubo + Apreendida',
        tone: critical === 0 ? 'good' : 'bad',
      },
    ],
    chartKind: 'pie',
    pieData,
    tableHeaders: ['Status', 'Qtd.', '% frota'],
    tableRows: pieData.map((row) => [
      row.name,
      String(row.value),
      total ? `${((row.value / total) * 100).toFixed(1)}%` : '0%',
    ]),
    footnote:
      'Agrupamento por status da locação / equipamento. Com dados reais, o sistema deve normalizar o cadastro para estes rótulos.',
  };
}

function scaleSeries(series: DiretoriaSeriesPoint[] | undefined, factor: number): DiretoriaSeriesPoint[] | undefined {
  if (!series) return undefined;
  return series.map((p) => ({
    ...p,
    valor: Math.max(0, Math.round(p.valor * factor)),
    valor2: p.valor2 != null ? Math.max(0, Math.round(p.valor2 * factor)) : undefined,
  }));
}

function cloneIndicator(ind: DiretoriaIndicatorMock, scale: number): DiretoriaIndicatorMock {
  if (ind.id === 'motos-status') {
    return buildMotosPorStatusIndicator(scale);
  }
  return {
    ...ind,
    series: scaleSeries(ind.series, scale),
    pieData: ind.pieData?.map((p) => ({ ...p, value: Math.max(0, Math.round(p.value * scale)) })),
  };
}

const DIRETORIA_INDICATORS_BASE: DiretoriaIndicatorMock[] = [
  {
    id: 'custo-moto',
    title: 'Custo por moto',
    shortDescription: 'Histórico acumulado médio e mensal (exemplo).',
    primaryKpiIndex: 0,
    kpis: [
      { label: 'Média mensal', value: 'R$ 1.284', hint: 'mock', tone: 'bad' },
      { label: 'Acumulado (12m)', value: 'R$ 14.892', hint: 'mock', tone: 'bad' },
      { label: 'Motos (base)', value: '186', hint: 'mock', tone: 'neutral' },
    ],
    chartKind: 'line',
    series: serieBase(1),
    tableHeaders: ['Marca / modelo', 'Custo médio (mock)', 'Qtd.'],
    tableRows: [
      ['Honda CG 160', 'R$ 1.120', '64'],
      ['Yamaha Fazer 250', 'R$ 1.410', '38'],
      ['BMW G 310', 'R$ 1.980', '12'],
    ],
    footnote:
      'Definição de custo e fonte de dados serão alinhadas na fase com integração / cadastro de custos.',
  },
  {
    id: 'faturamento-bruto',
    title: 'Faturamento bruto geral',
    shortDescription: 'Anual e mensal (valores ilustrativos).',
    primaryKpiIndex: 0,
    kpis: [
      { label: 'Anual (YTD)', value: 'R$ 8,42 mi', hint: 'mock', tone: 'good' },
      { label: 'Mês atual', value: 'R$ 712.400', hint: 'mock', tone: 'good' },
      { label: 'Var. vs mês ant.', value: '+6,2%', hint: 'mock', tone: 'good' },
    ],
    chartKind: 'bar',
    series: serieBase(4).map((p) => ({ ...p, valor: p.valor * 1200, valor2: (p.valor2 ?? 0) * 800 })),
    barSeriesKey: 'valor',
    tableHeaders: ['Unidade (demo)', 'Faturamento mês'],
    tableRows: [
      ['Matriz JP', 'R$ 312.000'],
      ['Filial Natal', 'R$ 228.400'],
      ['Filial FOR', 'R$ 172.000'],
    ],
    footnote: 'Na fase de dados reais: regra contábil (recebido vs competência) e escopo por empresa.',
  },
  {
    id: 'inadimplencia',
    title: 'Inadimplência',
    shortDescription: 'Títulos a receber vencidos — acumulado e mês (exemplo).',
    primaryKpiIndex: 0,
    kpis: [
      { label: 'Saldo vencido', value: 'R$ 184.200', hint: 'mock', tone: 'bad' },
      { label: 'Títulos vencidos', value: '127', hint: 'mock', tone: 'bad' },
      { label: 'Novos no mês', value: '23', hint: 'mock', tone: 'bad' },
    ],
    chartKind: 'bar',
    series: meses.slice(0, 8).map((name, i) => ({ name, valor: 12 + i * 3, valor2: 8 + i * 2 })),
    barSeriesKey: 'valor',
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo (mock)'],
    tableRows: [
      ['0–30 dias', '42', 'R$ 58.200'],
      ['31–60 dias', '28', 'R$ 44.100'],
      ['61+ dias', '57', 'R$ 81.900'],
    ],
    footnote: 'Aging e totais seguirão a mesma base usada em relatórios de cobrança.',
  },
  {
    id: 'infracoes',
    title: 'Infrações (multas)',
    shortDescription: 'Pagas, vencidas e a vencer — acumulado e mês (exemplo).',
    primaryKpiIndex: 0,
    kpis: [
      { label: 'Pagas (mês)', value: '34 · R$ 8.160', hint: 'mock', tone: 'good' },
      { label: 'Vencidas', value: '19 · R$ 14.250', hint: 'mock', tone: 'bad' },
      { label: 'A vencer', value: '41 · R$ 28.700', hint: 'mock', tone: 'neutral' },
    ],
    chartKind: 'line',
    series: serieBase(2).map((p) => ({ ...p, valor: p.valor + 5, valor2: p.valor2 })),
    tableHeaders: ['Estado', 'Qtd.', 'Valor (mock)'],
    tableRows: [
      ['Pagas', '128', 'R$ 186.400'],
      ['Vencidas', '44', 'R$ 62.800'],
      ['A vencer', '62', 'R$ 91.200'],
    ],
    footnote: 'Classificação de multa poderá replicar a heurística já usada no app (descrição / observações).',
  },
  {
    id: 'motos-alugadas',
    title: 'Motos alugadas',
    shortDescription: 'Acumulado e fluxo mensal (exemplo).',
    primaryKpiIndex: 0,
    kpis: [
      { label: 'Ativas (est.)', value: '412', hint: 'mock', tone: 'good' },
      { label: 'Novas no mês', value: '38', hint: 'mock', tone: 'good' },
      { label: 'Canceladas no mês', value: '29', hint: 'mock', tone: 'neutral' },
    ],
    chartKind: 'bar',
    series: serieBase(3),
    barSeriesKey: 'valor',
    tableHeaders: ['Mês', 'Entradas', 'Saídas'],
    tableRows: [
      ['Mês atual', '38', '29'],
      ['Mês anterior', '33', '31'],
    ],
    footnote: 'Critérios de “ativa” virão de locações importadas / ERP.',
  },
  {
    id: 'rentabilidade-moto',
    title: 'Rentabilidade média por moto',
    shortDescription: 'Histórico e anual (receita vs custo — exemplo).',
    primaryKpiIndex: 2,
    kpis: [
      { label: 'Média anual', value: 'R$ 2.140 / moto', hint: 'mock', tone: 'neutral' },
      { label: 'Últimos 12m', value: 'R$ 1.980 / moto', hint: 'mock', tone: 'neutral' },
      { label: 'Margem (demo)', value: '18,5%', hint: 'mock', tone: 'good' },
    ],
    chartKind: 'line',
    series: meses.map((name, i) => ({
      name,
      valor: 1500 + i * 45 + (i % 3) * 20,
      valor2: 900 + i * 30,
    })),
    tableHeaders: ['Componente', 'Valor (mock)'],
    tableRows: [
      ['Receita atribuída', 'R$ 3,82 mi'],
      ['Custos atribuídos', 'R$ 3,12 mi'],
    ],
    footnote: 'Com custos reais: receita ÷ motos e comparação com período anterior.',
  },
  {
    id: 'motos-disponiveis',
    title: 'Motos disponíveis',
    shortDescription: 'Por marca / modelo (exemplo).',
    primaryKpiIndex: 0,
    kpis: [
      { label: 'Disponíveis hoje', value: '94', hint: 'mock', tone: 'good' },
      { label: 'Top marca', value: 'Honda (38)', hint: 'mock', tone: 'neutral' },
      { label: 'Em manutenção', value: '12', hint: 'mock', tone: 'neutral' },
    ],
    chartKind: 'bar',
    series: [
      { name: 'Honda', valor: 38 },
      { name: 'Yamaha', valor: 24 },
      { name: 'BMW', valor: 14 },
      { name: 'Kawasaki', valor: 11 },
      { name: 'Outras', valor: 7 },
    ],
    barSeriesKey: 'valor',
    tableHeaders: ['Modelo', 'Disponíveis'],
    tableRows: [
      ['CG 160', '22'],
      ['Fazer 250', '14'],
      ['G 310 R', '9'],
    ],
    footnote: '“Disponível” seguirá o mapeamento de status do equipamento nos dados importados.',
  },
  buildMotosPorStatusIndicator(1),
  {
    id: 'locacao-local',
    title: 'Locação por bairro / cidade',
    shortDescription: 'Somatório por local do locatário (exemplo).',
    primaryKpiIndex: 1,
    kpis: [
      { label: 'Cidades (demo)', value: '6', hint: 'mock', tone: 'neutral' },
      { label: 'Top cidade', value: 'João Pessoa', hint: 'mock', tone: 'neutral' },
      { label: 'Contratos (mês)', value: '38', hint: 'mock', tone: 'good' },
    ],
    chartKind: 'bar',
    series: [
      { name: 'João Pessoa', valor: 142 },
      { name: 'Natal', valor: 98 },
      { name: 'Fortaleza', valor: 76 },
      { name: 'Recife', valor: 54 },
    ],
    barSeriesKey: 'valor',
    tableHeaders: ['Bairro', 'Cidade', 'Locações'],
    tableRows: [
      ['Bessa', 'João Pessoa', '28'],
      ['Lagoa Nova', 'Natal', '22'],
      ['Aldeota', 'Fortaleza', '19'],
    ],
    footnote: 'Exige endereço do locatário na base; pode vir de importação ERP em fase futura.',
  },
];

export function getDiretoriaIndicators(companyId: string, periodId: string): DiretoriaIndicatorMock[] {
  const scale = getScenarioScale(companyId, periodId);
  return DIRETORIA_INDICATORS_BASE.map((ind) => cloneIndicator(ind, scale));
}

/** Compatível com importações antigas: cenário padrão. */
export const DIRETORIA_INDICATORS_MOCK = getDiretoriaIndicators('all', 'mes');

export const DIRETORIA_DEMO_COMPANIES = [
  { id: 'all', label: 'Todas as unidades ' },
  { id: 'jp', label: 'Matriz — João Pessoa' },
  { id: 'natal', label: 'Filial — Natal' },
] as const;

export const DIRETORIA_DEMO_PERIODS = [
  { id: 'mes', label: 'Mês atual' },
  { id: 'ano', label: 'Mês anterior' },
  { id: '12m', label: 'Últimos 12 meses' },
] as const;

function executiveKpisMock(scale: number): DiretoriaExecutiveCard[] {
  const fmtMi = (n: number) => `R$ ${(n / 1e6).toFixed(2)} mi`;
  const faturamento = 8.42 * scale;
  return [
    {
      id: 'exec-faturamento-demo',
      label: 'Faturamento (mês)',
      value: fmtMi(faturamento * 1e6),
      sublabel: '',
      trend: '+6,2% vs mês',
      trendPositive: true,
      valueTone: 'good',
      source: 'mock',
    },
    {
      id: 'exec-valor-carteira',
      label: 'Valor em carteira',
      value: 'R$ 184.200',
      sublabel: '',
      trend: undefined,
      valueTone: 'bad',
      source: 'mock',
    },
    {
      id: 'exec-titulos-vencidos',
      label: 'Títulos vencidos',
      value: String(Math.round(127 * scale)),
      sublabel: 'Parciais + vencidos',
      valueTone: 'bad',
      source: 'mock',
    },
    {
      id: 'exec-clientes-inad',
      label: 'Clientes inadimplentes',
      value: String(Math.round(58 * scale)),
      sublabel: 'Com título vencido',
      valueTone: 'bad',
      source: 'mock',
    },
    {
      id: 'exec-aging-leve',
      label: 'Vencidos 1–2 dias',
      value: String(Math.round(42 * scale)),
      sublabel: 'Faixa leve',
      trend: 'Var. vs mês',
      valueTone: 'bad',
      source: 'mock',
    },
    {
      id: 'exec-receita-projetada',
      label: 'Receita projetada',
      value: 'R$ 0',
      sublabel: 'Hoje em diante',
      trend: undefined,
      valueTone: 'neutral',
      source: 'mock',
    },
  ];
}

export function getDiretoriaExecutiveKpis(companyId: string, periodId: string): DiretoriaExecutiveCard[] {
  return executiveKpisMock(getScenarioScale(companyId, periodId));
}

/** Série agregada receita vs meta (ilustrativa) para gráfico de evolução. */
export function getDiretoriaOverviewEvolucao(companyId: string, periodId: string): DiretoriaSeriesPoint[] {
  const factor = getScenarioScale(companyId, periodId);
  return meses.map((name, i) => ({
    name,
    valor: Math.round((520 + i * 14 + Math.sin(i / 2) * 48) * factor),
    valor2: Math.round((480 + i * 12 + Math.cos(i / 3) * 36) * factor * 0.85),
  }));
}

export function getDiretoriaOverviewAgingMock(companyId: string, periodId: string): DiretoriaSeriesPoint[] {
  const factor = getScenarioScale(companyId, periodId);
  return [
    { name: '1–2 dias', valor: Math.round(58200 * factor), valor2: Math.round(42 * factor) },
    { name: '3–6 dias', valor: Math.round(44100 * factor), valor2: Math.round(28 * factor) },
    { name: '7+ dias', valor: Math.round(81900 * factor), valor2: Math.round(57 * factor) },
  ];
}

export function getDiretoriaOverviewUnidadesMock(companyId: string, periodId: string): DiretoriaSeriesPoint[] {
  const factor = getScenarioScale(companyId, periodId);
  return [
    { name: 'Matriz JP', valor: Math.round(312 * factor), valor2: Math.round(312000 * factor) },
    { name: 'Natal', valor: Math.round(228 * factor), valor2: Math.round(228400 * factor) },
    { name: 'Fortaleza', valor: Math.round(172 * factor), valor2: Math.round(172000 * factor) },
    { name: 'Recife', valor: Math.round(98 * factor), valor2: Math.round(98000 * factor) },
  ];
}

export function getMotosStatusPieForOverview(companyId: string, periodId: string) {
  return buildMotosPorStatusIndicator(getScenarioScale(companyId, periodId)).pieData ?? [];
}
